// NOTICE: KAPTION-INTEGRATION
// Kaption Feature Flags Configuration - Separate from Chatwoot's built-in feature flags
//
// This configuration is for the Kaption Feature Flags system which is:
// - External API-based (flags.kaptionai.com)
// - Cached in localStorage (persistent across sessions)
// - Refreshed every 30 seconds
// - NOT to be confused with Chatwoot's built-in feature flags
//

export const KAPTION_FEATURE_FLAGS_CONFIG = {
  // API Configuration
  apiUrl: 'https://flags.kaptionai.com/platform/flags',
  timeout: 10000, // 10 seconds

  // Cache Configuration
  cacheTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds
  // Development: refresh every 5 seconds, Production: every 30 seconds
  refreshInterval:
    import.meta.env.MODE === 'development' ? 5 * 1000 : 30 * 1000,

  // Installation Configuration
  installId: 'chat.kaptionai.com',

  // Service enabled flag
  enabled: true,

  // Fallback flags when API is unavailable
  // These should match the defaults in config/kaption_features.yml
  fallbackFlags: {
    KAPTION_FEATURE_FLAGS_DEBUG: false,
    KAPTION_AI_SUGGESTIONS: true,
    KAPTION_AI_SUGGESTIONS_USE_MOCK: false,
    KAPTION_DASHBOARD_APPS_JWT: true,
    KAPTION_PLATFORM_APP_AUTO_LINKING: true,
    KAPTION_LIVE_TRANSLATION: true,
    KAPTION_COPILOT: true,
    KAPTION_LANGUAGE_DETECTION_HEURISTICS: true,
    KAPTION_ENHANCED_CAMPAIGN_BUILDER: true,
    KAPTION_AI_STUDIO_SIDEBAR: false,
    KAPTION_CONCURRENT_CAMPAIGN_SENDING: true,
    KAPTION_CAMPAIGN_MOCK_WHATSAPP_API: false,
    KAPTION_DIRECT_SEND: false, // WhatsApp Direct Send API for single-recipient template sends
    KAPTION_CAMPAIGN_SKIP_ASSISTANT: false, // Deliver campaign replies straight to inbox, bypassing the assistant
    KAPTION_SIDEBAR_STARRING: true,
    KAPTION_NOTE_CARDS: true,
    KAPTION_UTILITY_TEMPLATE_REPLYBOX: true,
    KAPTION_SCHEDULED_MESSAGES: true,
    KAPTION_WABA_TEMPLATE_REGISTRY: true,
    KAPTION_AUTO_PROVISION_TEMPLATES: true,
    KAPTION_WHATSAPP_2FA: false, // Kaption2FA — WhatsApp second factor + Rumo MCP binding
    KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT: true, // Enabled for development
    KAPTION_PLATFORM_CHAT_LIST: true, // Enabled for development
    KAPTION_CONVERSATION_SEARCH: true, // Single-pane full-text conversation search
    KAPTION_SHOW_SEND_SETTINGS: true, // Enabled for development (same as KAPTION_PLATFORM_CHAT_LIST)
    KAPTION_WHATSAPP_MARKDOWN_CONVERSION: true, // WhatsApp markdown conversion enabled by default
    KAPTION_ENHANCED_COMMAND_BAR: true,
    KAPTION_ACCESS_TOKENS: false,
    KAPTION_SALES_KANBAN: true, // Sales Funnel Kanban board view (enabled for development)
    KAPTION_ACTION_PILL: false, // Action pill at bottom of chat list (privacy mode + scheduled messages)
    KAPTION_PRIVACY_MODE: false, // Privacy mode toggles (blur chat list + messages)
    KAPTION_VIDEO_DIRECT_URLS: true, // Direct S3/R2 URLs for video streaming (iOS AVPlayer support)
    KAPTION_POST_RESOLUTION_COOLDOWN: true, // Post-resolution cooldown for assistant webhooks
    KAPTION_GROUPS: true, // Group conversation support with multiple contacts
    KAPTION_RESPONSE_POLICIES: true, // Response time policy management
    KAPTION_DELIVERY_WEBHOOKS: true, // Delivery webhooks for campaign message status notifications
    KAPTION_MESSAGE_BATCHING: false, // Quantity-first WhatsApp outbound aggregation (opt-in)
    KAPTION_CAMPAIGN_REPORTS: true, // Aggregate Campaign Reports dashboard
    KAPTION_USAGE_ANALYTICS: false, // usage-analytics.FLAG.1 — client-side product-usage telemetry (ships dark)
    KAPTION_WHATSAPP_BSUID_OBSERVE: false,
    KAPTION_WHATSAPP_BSUID_INBOUND_ALIASES: false,
    KAPTION_WHATSAPP_BSUID_CONVERSATION_REUSE: false,
    KAPTION_WHATSAPP_BSUID_COEXISTENCE_SYNC: false,
    KAPTION_WHATSAPP_BSUID_STATUS_ENRICHMENT: false,
    KAPTION_WHATSAPP_BSUID_OUTBOUND_RECIPIENT: false,
    KAPTION_WHATSAPP_BSUID_CAMPAIGNS: false,
    KAPTION_WHATSAPP_REQUEST_CONTACT_INFO: false,
    KAPTION_NEW_CONVERSATION_MODAL: false, // Template-first "New message" modal; opt-in per account
    KAPTION_SMART_ASSIGNMENT: false, // smart-assignment.FLAG.1 — delegated assignment resolver (ships dark)
  },
};

// NOTICE: KAPTION-INTEGRATION
// Flag definitions with metadata - mirrors config/kaption_features.yml
// This allows the debug screen to show all known flags with descriptions
export const KAPTION_FLAG_DEFINITIONS = {
  // Debug & Observability
  KAPTION_FEATURE_FLAGS_DEBUG: {
    description:
      'Enable the Kaption Feature Flags debug screen for inspecting flags',
    category: 'Debug',
    status: 'experimental',
    default: false,
  },

  // AI Features
  KAPTION_AI_SUGGESTIONS: {
    description:
      'Enable AI-powered response suggestions in conversation reply box',
    category: 'AI',
    status: 'experimental',
    default: false,
  },
  KAPTION_AI_SUGGESTIONS_USE_MOCK: {
    description:
      'Use mock data for AI suggestions instead of calling external API (for development)',
    category: 'AI',
    status: 'experimental',
    default: false,
  },

  // Dashboard Apps
  KAPTION_DASHBOARD_APPS_JWT: {
    description:
      'Enable JWT-based authentication for Dashboard Apps with minimal payload and 24-hour caching',
    category: 'Security',
    status: 'experimental',
    default: true,
  },

  // Platform Management
  KAPTION_PLATFORM_APP_AUTO_LINKING: {
    description:
      'Automatically grant Platform App permission to manage all newly created accounts',
    category: 'Dashboard',
    status: 'experimental',
    default: true,
  },

  // Translation
  KAPTION_LIVE_TRANSLATION: {
    description:
      'Enable live translation of messages between agents and customers',
    category: 'AI',
    status: 'experimental',
    default: true,
  },

  // Language Detection
  KAPTION_LANGUAGE_DETECTION_HEURISTICS: {
    description:
      'Enable heuristic-based language detection using phrase matching and phone country codes for faster detection',
    category: 'AI',
    status: 'stable',
    default: true,
  },

  // Campaigns
  KAPTION_ENHANCED_CAMPAIGN_BUILDER: {
    description:
      'Enable enhanced campaign builder with inline parameter binding and preview',
    category: 'Campaigns',
    status: 'experimental',
    default: true,
  },
  KAPTION_CONCURRENT_CAMPAIGN_SENDING: {
    description:
      'Enable concurrent message sending for WhatsApp campaigns with 60 MPS rate limiting and batch processing',
    category: 'Campaigns',
    status: 'experimental',
    default: true,
  },
  KAPTION_CAMPAIGN_MOCK_WHATSAPP_API: {
    description:
      'Use mock WhatsApp API for marketing campaigns (no real messages sent). For testing large campaigns with real data.',
    category: 'Campaigns',
    status: 'experimental',
    default: false,
  },
  KAPTION_DIRECT_SEND: {
    description:
      'Enable WhatsApp Direct Send API for sending template messages to individual phone numbers on demand',
    category: 'Campaigns',
    status: 'experimental',
    default: false,
  },
  KAPTION_CAMPAIGN_SKIP_ASSISTANT: {
    description:
      'Allow a campaign to deliver replies straight to the inbox (open + unassigned), bypassing the Kaption assistant',
    category: 'Campaigns',
    status: 'experimental',
    default: false,
  },

  // Kaption Copilot
  KAPTION_COPILOT: {
    description:
      'Enable Kaption Copilot in conversation sidebar for AI-powered agent assistance',
    category: 'AI',
    status: 'experimental',
    default: true,
  },

  // AI Studio
  KAPTION_AI_STUDIO_SIDEBAR: {
    description:
      'Enable the AI Studio sidebar with tree navigation inside the AI Studio iframe',
    category: 'AI Studio',
    status: 'experimental',
    default: false,
  },

  // UI Features
  KAPTION_SIDEBAR_STARRING: {
    description:
      'Enable per-user starring of sidebar items in conversations dropdown',
    category: 'UI',
    status: 'experimental',
    default: true,
  },
  KAPTION_NOTE_CARDS: {
    description:
      'Enable structured private-note cards rendered from markdown/text in conversation view',
    category: 'Conversations',
    status: 'stable',
    default: true,
  },

  // Utility Templates - ReplyBox
  KAPTION_UTILITY_TEMPLATE_REPLYBOX: {
    description:
      'Enable utility template features in ReplyBox (follow-up and scheduled messages)',
    category: 'Conversations',
    status: 'experimental',
    default: true,
  },

  // WABA Template Registry
  KAPTION_WABA_TEMPLATE_REGISTRY: {
    description:
      'Enable WABA-level template registry to prevent duplicate template creation across inboxes sharing the same WhatsApp Business Account',
    category: 'Templates',
    status: 'experimental',
    default: true,
  },

  // Auto-Provision Templates
  KAPTION_AUTO_PROVISION_TEMPLATES: {
    description:
      'Automatically create and sync Kaption utility templates when a new WhatsApp inbox is connected. Uses WABA registry to avoid duplicates.',
    category: 'Templates',
    status: 'experimental',
    default: true,
  },

  // WhatsApp Template Management
  KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT: {
    description:
      'Enable WhatsApp template CRUD (list, create, sync) in the Campaigns > WhatsApp > Templates tab',
    category: 'Templates',
    status: 'experimental',
    default: false,
  },

  // Scheduled Messages
  KAPTION_SCHEDULED_MESSAGES: {
    description:
      'Enable scheduling messages for future delivery in Reply and Follow-up modes with timezone support',
    category: 'Conversations',
    status: 'experimental',
    default: true,
  },

  // Platform Chat List
  KAPTION_PLATFORM_CHAT_LIST: {
    description:
      'Enable the new Kaption Platform Chat List with enhanced topbar (cycling filters, status tabs, favorites, folders, labels, channels)',
    category: 'UI',
    status: 'experimental',
    default: true,
  },
  KAPTION_CONVERSATION_SEARCH: {
    description:
      'Enable single-pane full-text conversation search with contact, message, and audio transcription matches',
    category: 'UI',
    status: 'experimental',
    default: true,
  },

  // Settings
  KAPTION_SHOW_SEND_SETTINGS: {
    description:
      'Show Undo Mode and Enter to Send settings in the Kaption topbar settings panel',
    category: 'Settings',
    status: 'experimental',
    default: true,
  },

  // Enhanced Command Bar
  KAPTION_ENHANCED_COMMAND_BAR: {
    description:
      'Enable enhanced command bar with settings toggles, navigation filters, search, and quick actions',
    category: 'UI',
    status: 'experimental',
    default: true,
  },

  // Access Tokens
  KAPTION_ACCESS_TOKENS: {
    description:
      'Enable Kaption Access Tokens feature for creating scoped API tokens linked to Assistants',
    category: 'Security',
    status: 'experimental',
    default: false,
  },

  // WhatsApp Markdown Conversion
  KAPTION_WHATSAPP_MARKDOWN_CONVERSION: {
    description:
      'Enable automatic conversion of CommonMark markdown to WhatsApp-compatible formatting when sending messages. Converts bold (**text** → *text*), italic, strikethrough, code blocks, links, and mentions.',
    category: 'WhatsApp',
    status: 'stable',
    default: true,
  },

  // Sales Funnel Kanban
  KAPTION_SALES_KANBAN: {
    description:
      'Enable Sales Funnel Kanban board view for conversations organized by deal pipeline stages',
    category: 'Sales',
    status: 'experimental',
    default: false,
  },

  // Action Pill
  KAPTION_ACTION_PILL: {
    description:
      'Enable the KaptionActionPill at the bottom of the chat list with privacy mode and scheduled messages access',
    category: 'UI',
    status: 'experimental',
    default: false,
  },

  // Privacy Mode
  KAPTION_PRIVACY_MODE: {
    description:
      'Enable privacy mode toggles that blur conversation cards in the chat list and message bubbles inside conversations',
    category: 'UI',
    status: 'experimental',
    default: false,
  },

  // Post-Resolution Cooldown
  KAPTION_POST_RESOLUTION_COOLDOWN: {
    description:
      'Enable post-resolution cooldown for Kaption assistant webhooks per inbox (3, 6, or 12 hours)',
    category: 'Settings',
    status: 'experimental',
    default: true,
  },

  // Video Streaming
  KAPTION_VIDEO_DIRECT_URLS: {
    description:
      'Use direct S3/R2 URLs for video files instead of Rails proxy URLs. This enables proper Range request support for iOS AVPlayer streaming.',
    category: 'Storage',
    status: 'experimental',
    default: true,
  },

  // Group Conversations
  KAPTION_GROUPS: {
    description:
      'Enable group conversation support with multiple contacts per conversation. Includes group creation, contact management, and filtering.',
    category: 'Conversations',
    status: 'experimental',
    default: true,
  },

  // Response Policies
  KAPTION_RESPONSE_POLICIES: {
    description:
      'Enable response time policy management with threshold tracking for first response, next response, and resolution times',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // Delivery Webhooks
  KAPTION_DELIVERY_WEBHOOKS: {
    description:
      'Enable Delivery Webhooks for sending campaign message status notifications (sent, delivered, read, failed, replied) to client endpoints',
    category: 'Integrations',
    status: 'experimental',
    default: true,
  },

  KAPTION_MESSAGE_BATCHING: {
    description:
      'Enable quantity-first WhatsApp outbound aggregation delivery modes',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // Product-Usage Analytics (usage-analytics.FLAG.1)
  KAPTION_USAGE_ANALYTICS: {
    description:
      'Enable client-side product-usage analytics (telemetry library + short-lived JWT mint). Metadata-only events; no end-customer PII.',
    category: 'Analytics',
    status: 'experimental',
    default: false,
  },

  KAPTION_WHATSAPP_BSUID_OBSERVE: {
    description: 'Emit redacted WhatsApp identity-shape telemetry only',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_INBOUND_ALIASES: {
    description: 'Resolve phone and BSUID aliases for inbound Cloud messages',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_CONVERSATION_REUSE: {
    description: 'Reuse conversations across aliases in one WhatsApp inbox',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_COEXISTENCE_SYNC: {
    description:
      'Enable BSUID-safe coexistence history and echo synchronization',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_STATUS_ENRICHMENT: {
    description: 'Enrich contacts from trusted BSUID status fields',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_OUTBOUND_RECIPIENT: {
    description: 'Use the Graph recipient field for one-to-one BSUID sends',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_BSUID_CAMPAIGNS: {
    description: 'Allow verified BSUID aliases in WhatsApp campaigns',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },
  KAPTION_WHATSAPP_REQUEST_CONTACT_INFO: {
    description:
      'Enable the phone-number request action and Meta request_contact_info payload for eligible WhatsApp Cloud contacts',
    category: 'WhatsApp',
    status: 'experimental',
    default: false,
  },

  // Smart Assignment (smart-assignment.FLAG.1)
  KAPTION_SMART_ASSIGNMENT: {
    description:
      'Enable smart assignment: the account delegates "who should own this conversation" to an HTTP endpoint it configures. Gates the settings section, the command-bar entry, the manual endpoint and the automatic pass on team assignment.',
    category: 'Settings',
    status: 'experimental',
    default: false,
  },

  // New Conversation Modal (glass-tile, template-first)
  KAPTION_NEW_CONVERSATION_MODAL: {
    description:
      'Enable the always-visible top-bar "New message" button and the glass-tile, template-first new-conversation modal (contact/raw-number → template gallery + live preview → fill vars → send). Flag OFF leaves today\'s composer/entry points unchanged.',
    category: 'Conversations',
    status: 'experimental',
    default: false,
  },
};
